//@ts-check
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import FeeDisplay from '../../../components/FeeDisplay';
import AmountTextField from '../../../components/FeeDisplay/AmountTextField';
import ACHForm from '../../../components/ACHForm/ACHForm';
import AddressFieldsDropDown from '../../../components/AddressFieldsDropDown/AddressFieldsDropDown2';
import TsepCard from '../../../components/CardForm/TsepCard';
import PaytraceCard from '../../../components/CardForm/PaytraceCard';
import TabPanel from './TabPanel';
import PaymentHeader from './PaymentHeader';
import { checkCreditOrDebit } from '../../../helpers/PostDataApi';
import { checkAddressEnabled, validateCityInAddress } from '../../../helpers/functions/addressFieldsEnabled';
import { changeFocus } from '../../../helpers/changeFocusCard';
import config from '../../../constants/config';
import commonStrings from '../../../constants/commonStrings';
import { initialAddressSettings, initialAddressState } from '../../../helpers/addressSettingsState';
import { usePaymentValidation } from '../hooks/usePaymentValidation';
import { useTsepEventHandler } from '../hooks/useTsepEventHandler';
import { buildPaymentData, clearEncryptedCardData } from '../../../helpers/paymentDataBuilder';
import locationsService from '../../../services/locationsService';
import sanitizePaymentTerms from '../../../helpers/sanitizePaymentTerms';

const FlexLib = require('@flex/lib');
const { formatCurrency } = FlexLib.utils.feeService;

const CREDIT_CARD_TYPES = new Set(['CREDIT', 'CHARGE CARD', 'AMERICAN EXPRESS']);

/** @constant Credit or Debit card types */
const CREDIT_OR_DEBIT_TYPES = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
};

/** @constant Tab indices for payment method selection */
const PAYMENT_TABS = {
  CARD: 0,
  ACH: 1,
};

/** @constant Tab type string identifiers */
const TAB_TYPES = {
  CARD: 'card',
  BANK_TRANSFER: 'bankTransfer',
};

/**
 * @constant
 * @type {Record<"REFUND" | "PRIVACY", "refund_policy" | "privacy_policy">} Policy document types */
const POLICY_TYPES = {
  REFUND: 'refund_policy',
  PRIVACY: 'privacy_policy',
};

/**
 * @component PaymentForm
 * @description A reusable payment form component for card and ACH payments.
 *
 * Features:
 * - Card payment with Paytrace or Multipass (TSEP) gateway
 * - ACH bank transfer payment
 * - Address field collection (billing/shipping)
 * - Fee calculation display
 * - Form validation
 * - BIN lookup for card type detection
 *
 * @param {PaymentFormProps} props - Component props
 * @returns {JSX.Element} Payment form UI
 */
const PaymentForm = ({
  // Required props
  location,
  feeSettings,
  termsConditions,
  baseAmount,
  onSubmit,
  isLoading,
  setErrors,
  fieldErrors,
  setFieldErrors,
  firstName,
  setFirstNameField,
  lastName,
  setLastNameField,

  // Optional props
  invoice = null,
  onViewInvoice = null,
  onViewPolicy = null,
  showInvoiceDetails = false,
  email = null,
  setEmailField = null,
  phone = null,
  setPhoneField = null,
  amount = null,
  setAmountField = null,
  invoiceNumber = null,
  setInvoiceNumberField = null,

  // TSEP state (managed by parent for gateway coordination)
  tsepFieldSuccess = false,
  setTsepFieldSuccess = null,
  tsepFieldError = null,
  setTsepFieldError = null,
}) => {
  /** @type {React.RefObject<HTMLInputElement>} Ref for expiry date input focus */
  const expiryDateRef = useRef(null);

  /** @type {React.RefObject<HTMLInputElement>} Ref for CSC input focus */
  const cscCardRef = useRef(null);

  /** @type {React.RefObject<ACHFormRef>} Ref for ACH form validation */
  const achFormRef = useRef(null);

  /** @type {React.RefObject<AddressFormRef>} Billing address form validation ref */
  const billingAddressFormValidationRef = useRef(null);

  /** @type {React.RefObject<AddressFormRef>} Shipping address form validation ref */
  const shippingAddressFormValidationRef = useRef(null);

  // Card field errors
  const [cardErrors, setCardErrors] = useState(
    /** @type {CardErrors} */ ({
      cardNumber: false,
      cardExpiryDate: false,
      cardCsc: false,
    })
  );

  // Payment tab state
  const [activeTab, setActiveTab] = useState(PAYMENT_TABS.CARD);
  /** @type {[any, React.Dispatch<React.SetStateAction<any>>]} */
  const [calculationResult, setCalculationResult] = useState(null);

  // Form input state
  /** @type {[CardData, React.Dispatch<React.SetStateAction<CardData>>]} */
  const [card, setCard] = useState({
    cardNumber: '',
    cardExpiryDate: '__/____',
    cardCsc: '___',
  });
  const [ach, setAch] = useState({
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    bankAccountType: '',
    bankName: '',
  });

  // Address state
  const [billingAddress, setBillingAddress] = useState({ ...initialAddressState.billingAddress });
  const [shippingAddress, setShippingAddress] = useState({ ...initialAddressState.shippingAddress });
  const [addressSettings, setAddressSettings] = useState({ ...initialAddressSettings });

  // Card detection state (from BIN lookup)
  const [creditOrDebit, setCreditOrDebit] = useState(/** @type {string|null} */ (null));
  const [binCodesResponse, setBinCodesResponse] = useState(/** @type {BinCodesResponseData} */ ({}));
  const [paymentType, setPaymentType] = useState(/** @type {PaymentType} */ (commonStrings.debit));

  // TSEP state (local, not from parent)
  /** @type {[TsepResponseData, React.Dispatch<React.SetStateAction<TsepResponseData>>]} */
  const [tsepResponse, setTsepResponse] = useState({});

  /** @type {string} Current payment gateway */
  const paymentGateway = location?.gateway || commonStrings.paytrace;

  /** @type {boolean} Whether ACH payments are enabled */
  const isAchEnabled = location?.ACH || false;

  /** @type {string|null|undefined} Disabled tab from settings */
  const disabledTab = feeSettings?.payLinkSettings?.disabledTab;

  /** @type {boolean} Whether dual fee pricing is enabled */
  const dualFeeEnabled = feeSettings?.DualFeeEnabled || false;

  /** @type {string} URL for location image */
  const locationImageUrl = locationsService.getLocationImageByIdUrl(location?._id || '');

  /**
   * @description Check if card is credit or debit using BIN lookup service.
   * This is the only external data fetching performed by PaymentForm.
   * Wrapped in useCallback to prevent stale closure when passed to event handlers.
   *
   * @param {string} cardNumber - Card number to check (minimum 6 digits)
   * @returns {Promise<void>}
   */
  const checkCardPaymentType = useCallback(async (cardNumber) => {
    cardNumber = cardNumber.replaceAll(/\D/g, ''); // Remove non-numeric characters
    if (!cardNumber || cardNumber.length < 6) return;

    try {
      const data = await checkCreditOrDebit(config.BIN_CHECKER_URL, config.BIN_API_KEY, cardNumber.substring(0, 6));

      const cardInfo = data.data;
      let type = cardInfo?.type;

      // American Express and Charge Cards are treated as Credit
      /** @type {PaymentType} */
      let paymentTypeValue;
      if (CREDIT_CARD_TYPES.has(type) || CREDIT_CARD_TYPES.has(cardInfo?.card)) {
        type = CREDIT_OR_DEBIT_TYPES.CREDIT;
        paymentTypeValue = commonStrings.credit;
      } else {
        type = CREDIT_OR_DEBIT_TYPES.DEBIT;
        paymentTypeValue = commonStrings.debit;
      }
      setCreditOrDebit(type || null);
      setBinCodesResponse(cardInfo || {});
      setPaymentType(paymentTypeValue);
    } catch (err) {
      console.error('BIN lookup error:', err);
    }
  }, []);

  useEffect(() => {
    // Clear encrypted card data when page closes, reloads, or component unmounts
    const handleBeforeUnload = () => {
      clearEncryptedCardData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearEncryptedCardData();
    };
  }, []);

  /**
   * @description Update address settings when fee settings change
   */
  useEffect(() => {
    if (feeSettings?.invoiceSettings) {
      setAddressSettings({
        addressSettings: {
          billingAddress: validateCityInAddress(feeSettings.invoiceSettings.billingAddress),
          shippingAddress:
            feeSettings.invoiceSettings.shippingAddress || initialAddressSettings.addressSettings.shippingAddress,
        },
      });
    }
  }, [feeSettings]);

  /**
   * @description Set default tab from fee settings
   */
  useEffect(() => {
    if (feeSettings?.payLinkSettings?.defaultTab) {
      const defaultTab = feeSettings.payLinkSettings.defaultTab === TAB_TYPES.CARD ? 0 : 1;
      const defaultPaymentType = defaultTab === 0 ? commonStrings.debit : commonStrings.ach;
      setActiveTab(defaultTab);
      setPaymentType(defaultPaymentType);
    }
  }, [feeSettings?.payLinkSettings?.defaultTab]);

  // TSEP event handling for Multipass gateway
  useTsepEventHandler({
    paymentGateway,
    setTsepResponse,
    setTsepFieldSuccess,
    setTsepFieldError,
    checkCardPaymentType,
  });

  // Payment validation functions
  const { validateForm } = usePaymentValidation({
    paymentGateway,
    activeTab,
    card,
    ach,
    firstName,
    email,
    invoiceNumber,
    amount,
    calculationResult,
    tsepResponse,
    setEmailField,
    setInvoiceNumberField,
    setAmountField,
    achFormRef,
    billingAddressFormValidationRef,
    shippingAddressFormValidationRef,
    setCardErrors,
    setFieldErrors,
    setErrors,
  });

  /**
   * @description Handle card input field changes
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
   */
  const handleCardChange = (e) => {
    const { id, value } = e.target;
    let processedValue = value;

    // Handle American Express card number length (15 digits)
    if (id === 'cardNumber' && value[0] === '3' && value.length === 16 && value[value.length - 1] === '_') {
      processedValue = value.slice(0, -1);
    }

    setCard((prev) => ({ ...prev, [id]: processedValue }));
    setCardErrors((prev) => ({ ...prev, [id]: false }));

    // Check card type on card number change
    if (id === 'cardNumber' && processedValue) {
      checkCardPaymentType(processedValue);
    }

    // Handle focus change to next field
    changeFocus(id, processedValue, expiryDateRef.current, cscCardRef.current);
  };

  /**
   * @description Handle ACH form field changes
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event
   */
  const handleAchChange = (e) => {
    const { id, value } = e.target;
    setAch((prev) => ({ ...prev, [id]: value }));
  };

  /**
   * @description Handle payment method tab change
   * @param {React.SyntheticEvent} _ - Event (unused)
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    let newPaymentType;
    if (newValue === PAYMENT_TABS.CARD) {
      newPaymentType = creditOrDebit?.toLowerCase() || commonStrings.debit;
    } else {
      newPaymentType = commonStrings.ach;
    }
    setPaymentType(newPaymentType);
  };

  /**
   * @description Handle form submission
   * @param {React.FormEvent} e - Form event
   */
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm(cardErrors)) {
      return;
    }

    // Extra safety check: prevent ACH submission with empty required fields
    if (activeTab === PAYMENT_TABS.ACH && (!ach.accountNumber?.trim() || !ach.routingNumber?.trim())) {
      setErrors(['Please fill in all required ACH fields before submitting.']);
      return;
    }

    try {
      const paymentData = await buildPaymentData({
        paymentGateway,
        activeTab,
        paymentType,
        firstName,
        lastName,
        card,
        ach,
        calculationResult,
        tsepResponse,
        binCodesResponse,
        billingAddress: /** @type {Address} */ (billingAddress),
        shippingAddress: /** @type {Address} */ (shippingAddress),
        setErrors,
      });

      if (paymentData) {
        await onSubmit(paymentData);
      }
    } catch (/** @type {Error | any} */ err) {
      setErrors([err?.message || 'An error occurred during payment processing. Please try again.']);
    }
  };

  /**
   * @description Handle policy document view
   * @param {"refund_policy" | "privacy_policy"} policyType - Type of policy to view
   */
  const handlePolicyClick = (policyType) => {
    if (onViewPolicy) {
      onViewPolicy(policyType);
    }
  };

  /**
   * @description Get card tab title based on dual fee settings
   * @returns {string} Tab title
   */
  const cardTabTitle = useMemo(() => {
    if (dualFeeEnabled) return 'Card';
    if (!creditOrDebit) return 'Card';
    return `${creditOrDebit} Card`;
  }, [dualFeeEnabled, creditOrDebit]);

  /**
   * @description Get payment title for amount display
   * @returns {string} Payment title
   */
  const paymentTitle = useMemo(() => {
    if (dualFeeEnabled) {
      return activeTab === PAYMENT_TABS.CARD ? 'Card Price' : 'ACH Price';
    }
    return 'Payment Amount';
  }, [dualFeeEnabled, activeTab]);

  /**
   * @description Determine if submit button should be disabled
   * @returns {boolean} True if button should be disabled
   */
  const isSubmitDisabled = useMemo(() => {
    if (isLoading) return true;
    if (activeTab === PAYMENT_TABS.CARD && paymentGateway === commonStrings.multipass) {
      return !tsepFieldSuccess;
    }
    return false;
  }, [isLoading, activeTab, paymentGateway, tsepFieldSuccess]);

  return (
    <Grid container spacing={[0, 2, 3]} sx={{ my: { xs: 0, sm: 2, md: 3 } }}>
      {/* TSEP Field Error Alert */}
      {tsepFieldError && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {tsepFieldError}
          </Alert>
        </Grid>
      )}
      {/* Left Column - Payment Form */}
      <Grid item xs={12} md={8}>
        <Card sx={{ p: { xs: 0, sm: 2 } }}>
          <CardContent>
            {/* Header with location logo and banner */}
            <PaymentHeader
              location={location}
              banner={termsConditions?.isPaymentPageBannerEnabled ? termsConditions.paymentPageBanner : null}
              locationImageUrl={locationImageUrl}
            />

            <form id="DemoForm" onSubmit={handleFormSubmit}>
              {/* Payment Amount Display */}
              <Box sx={{ mb: 3, mt: 3 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {paymentTitle}
                </Typography>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {/* calculationResult is set synchronously by FeeDisplay component before user interaction */}
                  {formatCurrency(calculationResult?.totalAmount || baseAmount)}
                </Typography>
              </Box>

              {/* Customer & Amount Input Section */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Invoice Number */}
                {invoiceNumber !== null && setInvoiceNumberField && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="invoiceNumber"
                      label={'Invoice Number'}
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumberField(e.target.value)}
                      fullWidth
                      required
                      error={!!fieldErrors?.invoiceNumber}
                      helperText={fieldErrors?.invoiceNumber ? 'Invoice number is required' : ''}
                      variant="outlined"
                      data-cy="invoiceNumber-input"
                    />
                  </Grid>
                )}
                {/* Amount */}
                {amount !== null && setAmountField && (
                  <Grid item xs={12} sm={6}>
                    <AmountTextField
                      id="amount"
                      label="Amount"
                      amount={amount}
                      setAmount={setAmountField}
                      fullWidth
                      required
                      error={!!fieldErrors?.amount}
                    />
                  </Grid>
                )}
                {/* First Name (always shown)*/}
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="firstName"
                    label={'First Name'}
                    value={firstName}
                    onChange={(e) => setFirstNameField && setFirstNameField(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    error={!!fieldErrors?.firstName}
                    helperText={fieldErrors?.firstName ? 'First name is required' : ''}
                    data-cy="firstName-input"
                  />
                </Grid>
                {/* Last Name (always shown)*/}
                <Grid item xs={12} sm={6}>
                  <TextField
                    id="lastName"
                    label={'Last Name'}
                    value={lastName}
                    onChange={(e) => setLastNameField && setLastNameField(e.target.value)}
                    fullWidth
                    variant="outlined"
                    data-cy="lastName-input"
                  />
                </Grid>

                {/* Email */}
                {email !== null && setEmailField && (
                  <Grid item xs={12}>
                    <TextField
                      id="email"
                      label={'Email'}
                      type="email"
                      value={email}
                      onChange={(e) => setEmailField(e.target.value)}
                      fullWidth
                      required
                      error={!!fieldErrors?.email}
                      helperText={fieldErrors?.email ? 'Email is required' : ''}
                      variant="outlined"
                      data-cy="email-input"
                    />
                  </Grid>
                )}

                {/* Phone */}
                {phone !== null && setPhoneField && feeSettings?.isPhoneNumber && (
                  <Grid item xs={12}>
                    <TextField
                      id="phone"
                      label={'Phone'}
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhoneField(e.target.value)}
                      fullWidth
                      variant="outlined"
                      data-cy="phone-input"
                    />
                  </Grid>
                )}
              </Grid>
              {/* Payment Method Tabs */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ backgroundColor: 'grey.100', borderRadius: 1, borderBottom: 1, borderColor: 'divider', mb: 2 }}
                data-cy="PaymentForm-tabs"
              >
                {disabledTab !== TAB_TYPES.CARD && (
                  <Tab label={cardTabTitle} value={PAYMENT_TABS.CARD} data-cy="PaymentForm-card-tab" />
                )}
                {isAchEnabled && disabledTab !== TAB_TYPES.BANK_TRANSFER && (
                  <Tab label="Bank Transfer" value={PAYMENT_TABS.ACH} data-cy="PaymentForm-ach-tab" />
                )}
              </Tabs>
              {/* Card Payment Tab */}
              <TabPanel value={activeTab} index={PAYMENT_TABS.CARD} hidden={disabledTab === TAB_TYPES.CARD}>
                <Grid container rowGap={3} justifyContent="center">
                  {/* Card Form - Gateway specific */}
                  <Grid item xs={12}>
                    {paymentGateway === commonStrings.multipass ? (
                      <TsepCard displayCardDetails={{ showCardInput: true }} isOneTimeCustomer={true} />
                    ) : (
                      <PaytraceCard
                        card={card}
                        cardErrors={cardErrors}
                        handleChangeCard={handleCardChange}
                        expiryDateRef={expiryDateRef}
                        cscCardRef={cscCardRef}
                        displayCardDetails={{ showCardInput: true, showSaveCheckbox: false }}
                      />
                    )}
                  </Grid>
                </Grid>
              </TabPanel>
              {/* ACH Payment Tab */}
              <TabPanel value={activeTab} index={PAYMENT_TABS.ACH} hidden={disabledTab === TAB_TYPES.BANK_TRANSFER}>
                <Grid container rowGap={3} justifyContent="center">
                  <Grid item xs={12}>
                    {/* ACHForm is a legacy component without proper TypeScript type definitions - using any cast */}
                    {React.createElement(/** @type {any} */ (ACHForm), {
                      ach,
                      handleChangeACH: handleAchChange,
                      variant: 'basicListTight',
                      location,
                      ref: achFormRef,
                    })}
                  </Grid>
                </Grid>
              </TabPanel>
              {/* Address Fields */}
              {(checkAddressEnabled(addressSettings?.addressSettings?.billingAddress) ||
                checkAddressEnabled(addressSettings?.addressSettings?.shippingAddress)) && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <AddressFieldsDropDown
                    type="all"
                    billingAddressFields={billingAddress}
                    shippingAddressFields={shippingAddress}
                    billingAddressSettings={addressSettings?.addressSettings?.billingAddress}
                    shippingAddressSettings={addressSettings?.addressSettings?.shippingAddress}
                    setBillingAddressFields={setBillingAddress}
                    setShippingAddressFields={setShippingAddress}
                    billingFormValidationRef={billingAddressFormValidationRef}
                    shippingFormValidationRef={shippingAddressFormValidationRef}
                    fieldAlignment="column"
                  />
                </Grid>
              )}
              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={<LockOutlinedIcon />}
                onClick={handleFormSubmit}
                disabled={isSubmitDisabled}
                sx={{ mt: 3, mb: 3 }}
                data-cy="PaymentForm-submit-button"
              >
                {'Pay'}
              </Button>

              {/* Authorization Text and Terms */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                  By clicking &apos;Pay&apos; I authorize {location?.locationName} to{' '}
                  {activeTab === PAYMENT_TABS.CARD ? 'charge my card' : 'debit my bank account by ACH'}{' '}
                  {formatCurrency(calculationResult?.totalAmount)} on {new Date().toDateString()}.
                  {onViewPolicy && (
                    <>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => handlePolicyClick(POLICY_TYPES.PRIVACY)}
                        sx={{ ml: 1 }}
                        data-cy="PaymentForm-privacy-link"
                      >
                        Privacy Statement
                      </Link>
                      <Link
                        component="button"
                        type="button"
                        onClick={() => handlePolicyClick(POLICY_TYPES.REFUND)}
                        sx={{ ml: 1 }}
                        data-cy="PaymentForm-refund-link"
                      >
                        Refund Policy
                      </Link>
                    </>
                  )}
                </Typography>

                {/* Terms and Conditions */}
                {termsConditions?.isPaymentTermConditionEnabled && (
                  <Box
                    sx={{ mt: 2 }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizePaymentTerms(termsConditions.paymentTermCondition),
                    }}
                  />
                )}
              </Box>
            </form>
          </CardContent>
        </Card>
      </Grid>

      {/* Right Column - Fee Display & Invoice Details */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            {/* View Invoice Button (only for invoice payments) */}
            {/* Parent provides generic function type, MUI Button expects MouseEventHandler<HTMLButtonElement> */}
            {showInvoiceDetails && onViewInvoice ? (
              <Button
                fullWidth
                variant="outlined"
                onClick={/** @type {any} */ (onViewInvoice)}
                sx={{ mb: 3 }}
                data-cy="PaymentForm-view-invoice-button"
              >
                {'View Invoice'}
              </Button>
            ) : (
              // Button purposely omits click handler. Displayed as button for consistency with UI of invoice paylink
              <Button
                fullWidth
                variant="outlined"
                sx={{ mb: 3, pointerEvents: 'none' }}
                data-cy="PaymentForm-payment-details-button"
              >
                {'Payment Details'}
              </Button>
            )}

            {/* Invoice Details Table (only for invoice payments) */}
            {showInvoiceDetails && invoice && (
              <Table size="small" sx={{ mb: 3 }}>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">{invoice.customFieldEnabled ? invoice.invoiceLabel : 'Invoice'}</TableCell>
                    <TableCell align="right" sx={{ wordBreak: 'break-all' }}>
                      {invoice.invoice || invoice.jobId}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left">Due Date</TableCell>
                    <TableCell align="right">
                      {invoice.dueDate ? new Date(invoice.dueDate).toDateString() : '—'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {/* Fee Calculator Display */}
            <FeeDisplay
              viewAllPrices={false}
              feeSettings={feeSettings}
              baseAmount={baseAmount?.toString() || '0.00'}
              paymentType={paymentType}
              state={billingAddress?.state}
              setCalculationResult={setCalculationResult}
              setError={(err) => setErrors((/** @type {string[]} */ prev) => [...prev, err.message])}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

PaymentForm.propTypes = {
  location: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    locationName: PropTypes.string,
    gateway: PropTypes.oneOf(['paytrace', 'multipass']),
    ACH: PropTypes.bool,
    image: PropTypes.string,
    multipassCredentials: PropTypes.object,
  }).isRequired,
  feeSettings: PropTypes.object.isRequired,
  termsConditions: PropTypes.object,
  baseAmount: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setErrors: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object,
  setFieldErrors: PropTypes.func.isRequired,
  invoice: PropTypes.shape({
    _id: PropTypes.string,
    invoice: PropTypes.string,
    amount: PropTypes.number,
    dueDate: PropTypes.string,
    customFieldEnabled: PropTypes.bool,
    invoiceLabel: PropTypes.string,
    jobId: PropTypes.string,
    isOneTimeCustomer: PropTypes.bool,
    oneTimeCustomerDetails: PropTypes.object,
  }),
  customer: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  onViewInvoice: PropTypes.func,
  onViewPolicy: PropTypes.func,
  showInvoiceDetails: PropTypes.bool,
  firstName: PropTypes.string.isRequired,
  setFirstNameField: PropTypes.func,
  lastName: PropTypes.string.isRequired,
  setLastNameField: PropTypes.func,
  email: PropTypes.string,
  setEmailField: PropTypes.func,
  phone: PropTypes.string,
  setPhoneField: PropTypes.func,
  amount: PropTypes.string,
  setAmountField: PropTypes.func,
  invoiceNumber: PropTypes.string,
  setInvoiceNumberField: PropTypes.func,
  tsepFieldSuccess: PropTypes.bool,
  setTsepFieldSuccess: PropTypes.func,
  tsepFieldError: PropTypes.string,
  setTsepFieldError: PropTypes.func,
};

PaymentForm.defaultProps = {
  invoice: null,
  customer: null,
  email: null,
  setEmailField: null,
  phone: null,
  setPhoneField: null,
  amount: null,
  setAmountField: null,
  invoiceNumber: null,
  setInvoiceNumberField: null,
  onViewInvoice: null,
  onViewPolicy: null,
  showInvoiceDetails: false,
  termsConditions: null,
  tsepFieldSuccess: false,
  setTsepFieldSuccess: null,
  tsepFieldError: null,
  setTsepFieldError: null,
};

export default PaymentForm;

/** @typedef {import('../../../../../types/common').Address} Address */
/** @typedef {import('../../../../../types/PaymentForm').TsepTokenEvent} TsepTokenEvent */
/** @typedef {import('../../../../../types/PaymentForm').TsepFieldValidationErrorEvent} TsepFieldValidationErrorEvent */
/** @typedef {import('../../../../../types/PaymentForm').TsepFocusOutEvent} TsepFocusOutEvent */
/** @typedef {import('../../../../../types/PaymentForm').TsepEvent} TsepEvent */
/** @typedef {import('../../../../../types/PaymentForm').CardErrors} CardErrors */
/** @typedef {import('../../../../../types/PaymentForm').CardData} CardData */
/** @typedef {import('../../../../../types/PaymentForm').TsepResponseData} TsepResponseData */
/** @typedef {import('../../../../../types/PaymentForm').BinCodesResponseData} BinCodesResponseData */
/** @typedef {import('../../../../../types/PaymentForm').ACHFormRef} ACHFormRef */
/** @typedef {import('../../../../../types/PaymentForm').AddressFormRef} AddressFormRef */
/** @typedef {import('../../../../../types/PaymentForm').InvoiceData} InvoiceData */
/** @typedef {import('../../../../../types/PaymentForm').CustomerData} CustomerData */
/** @typedef {import('../../../../../types/PaymentForm').TermsConditions} TermsConditions */
/** @typedef {import('../../../../../types/PaymentForm').PaymentFormData} PaymentFormData */
/** @typedef {import('../../../../../types/PaymentForm').PaymentFormProps} PaymentFormProps */
/** @typedef {import('../../../../../types/fees').DatabaseFeeSettings} DatabaseFeeSettings */
/** @typedef {import('../../../../../types/fees').PaymentType} PaymentType */
/** @typedef {import('../../../../../types/locations').LocationViewModelWithTsepFormData} LocationViewModel */
